import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Label,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './Protocol.ExportDerivedPatients.Form.Functions';
import { DateTimeInput } from '../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import './Protocol.ExportDerivedPatients.Form.Styles.css';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProtocolExportDerivedPatientsFormComponentCode = (props: ComponentFunctions.Props & DrawerProps) => {
  const [t] = useTranslation();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // Auto-download when the blob is ready and close the drawer
  useEffect(() => {
    if (props.FileBlob) {
      downloadFile(props.FileBlob);
      setTimeout(() => {
        props.onClose();
      }, 500);
    }
  }, [props.FileBlob]);

  const downloadFile = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DerivedPatients_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    props.ExportDerivedPatientsToExcel(undefined, dateFrom, dateTo);
  };

  const isFormValid = () => {
    return dateFrom && dateTo && dateTo >= dateFrom;
  };

  return (
    <OverlayDrawer open={props.isOpen} position="end" size="small">
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={props.onClose}
            />
          }
        >
          <Label size="large" weight="semibold">
            Exportar Pacientes Derivados
          </Label>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {props.error && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Error al exportar</MessageBarTitle>
                {props.error.ErrorMessage}
              </MessageBarBody>
            </MessageBar>
          )}

          <div style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
            <DateTimeInput
              label={t('protocolExportVisitForm.dateFrom')}
              placeHolder={t('protocolExportVisitForm.selectStartDate')}
              selectedDate={dateFrom}
              isSubmitted={false}
              isRequired={true}
              errorMessage={undefined}
              onSelectDate={setDateFrom}
            />
            <DateTimeInput
              label={t('protocolExportVisitForm.dateTo')}
              placeHolder={t('protocolExportVisitForm.selectEndDate')}
              selectedDate={dateTo}
              isSubmitted={false}
              isRequired={true}
              errorMessage={undefined}
              onSelectDate={setDateTo}
            />
          </div>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          <Button appearance="secondary" onClick={props.onClose}>
            {t('protocolExportVisitForm.cancel')}
          </Button>
          <Button
            appearance="primary"
            onClick={handleExport}
            disabled={!isFormValid() || props.isGenerating}
          >
            {t('protocolExportVisitForm.exportToExcel')}
          </Button>
          {props.isGenerating && (
            <Spinner label={t('protocolExportVisitForm.generatingFile')} />
          )}
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ProtocolExportDerivedPatientsFormComponentCode as any);
